/* ==========================================================================
   Blinq · Nav (sections/blinq-nav.liquid)
   - Desktop: dropdowns open on hover (as on blinq.me) and on click / Enter.
   - Tablet & phones (≤991px): the menu button opens a full-screen menu;
     dropdowns open and close on tap. The page behind doesn't scroll.
   - Sticky bar: publishes --header-height for Concept, and can hide on
     scroll down.
   - Logo: plays the blinq.me Lottie "blink" on hover, if one is set.
   - Theme editor: selecting a Column or Card block opens its dropdown.
   ========================================================================== */
(() => {
  const DESKTOP = window.matchMedia('(min-width: 992px)');
  const CAN_HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');
  const LOTTIE_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie_light.min.js';

  let lottieLoading = null;
  const loadLottie = () => {
    if (window.lottie) return Promise.resolve(window.lottie);
    if (!lottieLoading) {
      lottieLoading = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = LOTTIE_SRC;
        s.async = true;
        s.onload = () => (window.lottie ? resolve(window.lottie) : reject());
        s.onerror = reject;
        document.head.appendChild(s);
      });
    }
    return lottieLoading;
  };

  const init = (root) => {
    if (!root || root.dataset.navReady) return;
    root.dataset.navReady = 'true';

    const section = root.closest('.shopify-section');
    const html = document.documentElement;
    const dropdowns = [...root.querySelectorAll('[data-nav-dropdown]')];
    const burger = root.querySelector('[data-nav-burger]');

    /* ---- Dropdowns ---- */
    const timers = new WeakMap();
    const isOpen = (dd) => dd.classList.contains('is-open');
    const open = (dd) => {
      clearTimeout(timers.get(dd));
      dropdowns.forEach((other) => other !== dd && close(other, true));
      if (isOpen(dd)) return;
      dd.classList.remove('is-closing');
      dd.classList.add('is-open');
      const btn = dd.querySelector('.nav-btn--dropdown');
      if (btn) btn.setAttribute('aria-expanded', 'true');
    };
    const close = (dd, instant) => {
      clearTimeout(timers.get(dd));
      if (!isOpen(dd)) return;
      dd.classList.remove('is-open');
      const btn = dd.querySelector('.nav-btn--dropdown');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      if (instant || !DESKTOP.matches) { dd.classList.remove('is-closing'); return; }
      dd.classList.add('is-closing');
      timers.set(dd, setTimeout(() => dd.classList.remove('is-closing'), 160));
    };
    const closeAll = () => dropdowns.forEach((dd) => close(dd));

    dropdowns.forEach((dd) => {
      const btn = dd.querySelector('.nav-btn--dropdown');
      dd.addEventListener('mouseenter', () => {
        if (DESKTOP.matches && CAN_HOVER.matches) open(dd);
      });
      dd.addEventListener('mouseleave', () => {
        if (!DESKTOP.matches || !CAN_HOVER.matches) return;
        clearTimeout(timers.get(dd));
        timers.set(dd, setTimeout(() => close(dd), 120));
      });
      if (btn) {
        btn.addEventListener('click', () => {
          /* On desktop with a mouse, hover already opened it: a click keeps it open */
          if (DESKTOP.matches && CAN_HOVER.matches && isOpen(dd)) return;
          isOpen(dd) ? close(dd) : open(dd);
        });
      }
      /* Keyboard: tabbing out of an open panel closes it */
      dd.addEventListener('focusout', (e) => {
        if (DESKTOP.matches && !dd.contains(e.relatedTarget)) close(dd);
      });
    });

    document.addEventListener('click', (e) => {
      if (DESKTOP.matches && !e.target.closest('[data-nav-dropdown]')) closeAll();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const openDd = dropdowns.find(isOpen);
      if (openDd) {
        close(openDd);
        const btn = openDd.querySelector('.nav-btn--dropdown');
        if (btn) btn.focus();
      } else if (root.classList.contains('is-menu-open')) {
        setMenu(false);
        if (burger) burger.focus();
      }
    });

    /* ---- Full-screen menu (≤991px) ---- */
    const setMenu = (on) => {
      if (on) {
        if (section) section.classList.remove('header-hidden');
        const side = root.querySelector('.nav-side-wrap');
        if (side) root.style.setProperty('--nav-menu-top', Math.max(0, Math.round(side.getBoundingClientRect().bottom)) + 'px');
      }
      root.classList.toggle('is-menu-open', on);
      html.classList.toggle('nav-scroll-lock', on);
      if (burger) burger.setAttribute('aria-expanded', String(on));
      if (section) section.classList.remove('header-hidden');
      if (!on) closeAll();
    };
    if (burger) burger.addEventListener('click', () => setMenu(!root.classList.contains('is-menu-open')));
    const onBreakpoint = () => {
      if (DESKTOP.matches) setMenu(false);
      dropdowns.forEach((dd) => close(dd, true));
    };
    if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', onBreakpoint);
    else if (DESKTOP.addListener) DESKTOP.addListener(onBreakpoint);

    /* ---- Header height + sticky behaviour (Concept hooks) ---- */
    const bar = root.querySelector('.nav-side-wrap');
    const sticky = root.dataset.sticky;
    const setHeight = () => {
      html.style.setProperty('--header-height', root.offsetHeight + 'px');
      if (sticky && bar) html.style.scrollPaddingTop = (root.offsetHeight + 16) + 'px';
    };
    setHeight();
    if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(root);

    if (sticky && section) {
      section.classList.add('header-sticky');
      section.dataset.stickyType = sticky;
      if (sticky === 'on-scroll-up') {
        let lastY = window.scrollY;
        let ticking = false;
        const update = () => {
          const y = window.scrollY;
          const busy = root.classList.contains('is-menu-open') || dropdowns.some(isOpen);
          const past = y > root.offsetHeight * 2;
          if (past && y > lastY + 4 && !busy) section.classList.add('header-hidden');
          else if (y < lastY - 4 || !past) section.classList.remove('header-hidden');
          lastY = y;
          ticking = false;
        };
        window.addEventListener('scroll', () => {
          if (!ticking) { ticking = true; requestAnimationFrame(update); }
        }, { passive: true });
        root.addEventListener('focusin', () => section.classList.remove('header-hidden'));
      }
    }

    /* ---- Logo blink (Lottie), desktop with a mouse only ---- */
    const logoBox = root.querySelector('.nav-logo-img[data-lottie]');
    if (logoBox && CAN_HOVER.matches && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const start = () => {
        loadLottie().then((lottie) => {
          const holder = document.createElement('div');
          holder.className = 'nav-logo-lottie';
          holder.setAttribute('aria-hidden', 'true');
          logoBox.appendChild(holder);
          const anim = lottie.loadAnimation({
            container: holder,
            renderer: 'svg',
            loop: false,
            autoplay: false,
            path: logoBox.dataset.lottie,
          });
          anim.addEventListener('DOMLoaded', () => {
            anim.goToAndStop(0, true);
            logoBox.classList.add('is-lottie');
            const link = logoBox.closest('a') || logoBox;
            link.addEventListener('mouseenter', () => anim.goToAndPlay(0, true));
          });
          anim.addEventListener('data_failed', () => holder.remove());
        }).catch(() => {});
      };
      if (document.readyState === 'complete') setTimeout(start, 300);
      else window.addEventListener('load', () => setTimeout(start, 300), { once: true });
    }

    /* ---- Theme editor: show the panel of the block being edited ---- */
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:block:select', (e) => {
        const dd = e.target.closest && e.target.closest('[data-nav-dropdown]');
        if (!dd || !root.contains(dd)) return;
        if (!DESKTOP.matches) setMenu(true);
        open(dd);
      });
      document.addEventListener('shopify:block:deselect', (e) => {
        const dd = e.target.closest && e.target.closest('[data-nav-dropdown]');
        if (dd && root.contains(dd)) close(dd, true);
      });
    }
  };

  const initAll = () => document.querySelectorAll('[data-blinq-nav]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', (e) => init(e.target.querySelector('[data-blinq-nav]')));
  document.addEventListener('shopify:section:unload', () => document.documentElement.classList.remove('nav-scroll-lock'));
})();